import type { TaskFormData } from '../schemas/task-schema'
import { AnalysisType, BatchData, Contracts } from 'prove-common'

const isValidAnalysisType = (
  analysisType?: AnalysisType,
  productId?: number,
  matrixId?: number,
  payloadSchemaId?: number,
  substanceIds?: number[]
): boolean => {
  if (!analysisType) return false
  // Check if there is a valid sampleType for the given pair of product and matrix
  if (productId && analysisType.sampleType?.productId !== productId)
    return false
  if (matrixId && analysisType.sampleType?.matrixId !== matrixId) return false

  if (payloadSchemaId && analysisType.payloadSchema?.id !== payloadSchemaId)
    return false
  return !(
    substanceIds &&
    !substanceIds.every((id) =>
      analysisType.substances?.some((s) => s.substance.id === id)
    )
  )
}

export const findValidContractAnalysisTypes = ({
  formData,
  contracts
}: {
  formData: TaskFormData | Partial<TaskFormData> | undefined
  contracts: Contracts | undefined
}): Contracts => {
  if (!contracts) return []
  const {
    productId,
    matrixId,
    substanceIds,
    labId,
    payloadSchemaId,
    contractId
  } = formData || {}
  return contracts
    .filter((contract) => {
      if (contractId) {
        return contract.id === contractId
      }
      if (labId) {
        return contract.labId === labId
      }
      return true
    })
    .map((contract) => {
      const analysisTypes =
        contract.analysisTypes?.filter((analysisType) =>
          isValidAnalysisType(
            analysisType?.analysisType,
            productId,
            matrixId,
            payloadSchemaId,
            substanceIds
          )
        ) || []
      return analysisTypes.length > 0
        ? {
            title: contract.title,
            labId: contract.labId,
            id: contract.id,
            analysisTypes
          }
        : null
    })
    .filter((contract) => contract !== null)
}

export const filterBatchData = (
  batchData: BatchData,
  validAnalysisTypes: Contracts
): {
  valid: BatchData
  invalid: BatchData
} => {
  const validProductIds = new Set(
    validAnalysisTypes.flatMap((validAnalysisType) =>
      validAnalysisType.analysisTypes?.map(
        (analysisType) => analysisType.analysisType?.sampleType?.productId
      )
    )
  )
  const validMatrixIds = new Set(
    validAnalysisTypes.flatMap((validAnalysisType) =>
      validAnalysisType.analysisTypes?.map(
        (analysisType) => analysisType.analysisType?.sampleType?.matrixId
      )
    )
  )
  const validSampleTypeIds = new Set(
    validAnalysisTypes.flatMap((validAnalysisType) =>
      validAnalysisType.analysisTypes?.map(
        (analysisType) => analysisType.analysisType?.sampleType?.id
      )
    )
  )
  const validSubstanceIds = new Set(
    validAnalysisTypes.flatMap((validAnalysisType) =>
      validAnalysisType.analysisTypes?.flatMap((analysisType) =>
        analysisType.analysisType?.substances?.map(
          (substance) => substance.substance.id
        )
      )
    )
  )
  const validPayloadSchemaIds = new Set(
    validAnalysisTypes.flatMap((validAnalysisType) =>
      validAnalysisType.analysisTypes?.flatMap(
        (analysisType) => analysisType.analysisType?.payloadSchema?.id
      )
    )
  )

  const valid: BatchData = {
    ...batchData,
    products: batchData.products.filter((product) =>
      validProductIds.has(product.id)
    ),
    matrices: batchData.matrices.filter((matrix) =>
      validMatrixIds.has(matrix.id)
    ),
    sampleTypes: batchData.sampleTypes.filter((sampleType) =>
      validSampleTypeIds.has(sampleType.id)
    ),
    substances: batchData.substances.filter((substance) =>
      validSubstanceIds.has(substance.id)
    ),
    payloadSchemas: batchData.payloadSchemas.filter((payloadSchema) =>
      validPayloadSchemaIds.has(payloadSchema.id)
    )
  }

  // Invalid should only contain the subsets that are actually invalid; all other lists are empty.
  const invalid: BatchData = {
    products: batchData.products.filter(
      (product) => !validProductIds.has(product.id)
    ),
    matrices: batchData.matrices.filter(
      (matrix) => !validMatrixIds.has(matrix.id)
    ),
    substances: batchData.substances.filter(
      (substance) => !validSubstanceIds.has(substance.id)
    ),
    sampleTypes: batchData.sampleTypes.filter(
      (sampleType) => !validSampleTypeIds.has(sampleType.id)
    ),
    payloadSchemas: batchData.payloadSchemas.filter(
      (payloadSchema) => !validPayloadSchemaIds.has(payloadSchema.id)
    ),
    labs: [],
    analysisTypes: [],
    operations: [],
    sampnts: [],
    ordningsverdier: []
  }

  return { valid, invalid }
}
